<template>
    <div
        class="tw:flex tw:flex-col tw:gap-3 tw:rounded-xl tw:border tw:border-slate-200 tw:bg-white tw:p-4 tw:shadow-sm"
        :data-cy="'sesion-row-' + sesion.id"
    >
        <!-- Cabecera: pills (fecha, provisional, apertura) + título de la sesión -->
        <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:border-b tw:border-slate-100 tw:pb-2.5">
            <Pill
                variant="amber"
                :icon="sesion.provisional ? 'fas fa-question-circle' : 'far fa-clock'"
                :label="fechaFormateada"
            />

            <!-- Fecha provisional: misma forma que la pill de fecha, otro color -->
            <Pill
                v-if="sesion.provisional"
                :data-cy="'sesion-provisional-' + sesion.id"
                variant="violet"
                icon="fas fa-question-circle"
                label="Fecha provisional"
            />

            <!-- Apertura de puertas: pill neutra junto al resto de pills de la
                 sesión. `doors_open_at` es texto libre del v2 ("Nunca",
                 "Ahora", "1 hora antes"), así que se pinta tal cual. -->
            <Pill
                v-if="sesion.doors_open_at"
                :data-cy="'sesion-apertura-' + sesion.id"
                variant="slate"
                weight="medium"
                icon="fas fa-door-open"
                :label="'Apertura: ' + sesion.doors_open_at"
            />

            <!-- Separación pills → título: divisor fino en vez de un guion de
                 texto. Un `-` literal ensucia el textContent (copy/paste,
                 lectores de pantalla) y no aporta jerarquía; el peso y el
                 tamaño del h3 ya marcan que es el título. -->
            <span
                v-if="sesion.text"
                class="tw:h-4 tw:w-px tw:shrink-0 tw:bg-slate-200"
                aria-hidden="true"
            ></span>

            <!-- Texto identificativo de la sesión, cuando lo trae el v2 -->
            <h3
                v-if="sesion.text"
                :data-cy="'sesion-texto-' + sesion.id"
                class="tw:m-0 tw:min-w-0 tw:truncate tw:text-[15px] tw:font-semibold tw:text-slate-900"
            >
                {{ sesion.text }}
            </h3>
        </div>

        <!-- Acciones: principales visibles + el resto agrupado en Opciones.
             En tablet y escritorio la botonera se pega al borde derecho de la
             fila; en móvil sigue apilada a ancho completo, sin cambios. -->
        <div
            class="tw:flex tw:flex-col tw:gap-1.5 tw:sm:flex-row tw:sm:flex-wrap tw:sm:items-center tw:sm:justify-end"
            :data-cy="'sesion-acciones-' + sesion.id"
        >
            <Button
                v-if="permisos['eventos.vender'] && puedeVenderEnEvento"
                as="router-link"
                variant="default"
                icon="fas fa-euro-sign"
                custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                :to="{ name: 'back.events.venta', params: params }"
            >
                Venta
            </Button>
            <Button
                v-if="permisos['eventos.editar']"
                as="router-link"
                variant="default"
                icon="fas fa-lock"
                custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                :to="{ name: 'back.events.sesion.bloquear-asientos', params: params }"
            >
                Gestor butacas
            </Button>

            <!-- Solo en eventos actuales: configurar precios de la sesión -->
            <Button
                v-if="variant === 'actuales' && (permisos['eventos.crear'] || permisos['eventos.editar'])"
                as="router-link"
                variant="default"
                icon="fas fa-cogs"
                custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                :data-cy="'precios-evento-' + evento.id + '-sesion-' + sesion.id"
                :to="{ name: 'back.events.sesion.configuracion', params: params }"
            >
                Precios
            </Button>

            <!-- Listado de ventas de la sesión: sale de OPCIONES y pasa a botón
                 visible, en el hueco que dejan Editar y Borrar. Conserva el
                 gate que tenía en el desplegable. -->
            <Button
                v-if="variant === 'actuales' && permisos['eventos.informe']"
                as="router-link"
                variant="default"
                icon="fas fa-ticket-alt"
                custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                :to="{ name: 'back.events.ventas', params: params }"
            >
                Listado ventas
            </Button>

            <!-- Solo en eventos pasados: informes y ventas de la sesión.
                 Sin gate de permisos: el listado de pasados los mostraba
                 siempre, y ese comportamiento se conserva. -->
            <Button
                v-if="variant === 'pasados'"
                as="router-link"
                variant="default"
                icon="fas fa-ticket-alt"
                custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                :to="{ name: 'back.events.ventas', params: params }"
            >
                Listado ventas
            </Button>
            <Button
                v-if="variant === 'pasados'"
                as="router-link"
                variant="default"
                icon="fas fa-chart-bar"
                custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                :to="{ name: 'back.events.sesion.informe.control-acceso', params: params }"
            >
                Informe control de accesos
            </Button>

            <!-- Solo en eventos pasados: productos y códigos QR como botones -->
            <Button
                v-if="variant === 'pasados' && permisos['eventos.editar']"
                as="router-link"
                variant="default"
                icon="fas fa-shopping-bag"
                custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                :to="{ name: 'back.events.sesion.productos', params: params }"
            >
                Productos
            </Button>
            <Button
                v-if="variant === 'pasados' && permisos['eventos.editar']"
                as="router-link"
                variant="default"
                icon="fas fa-qrcode"
                custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                :to="{ name: 'back.events.sesion.codigos-qr', params: params }"
            >
                Códigos QR
            </Button>

            <OptionsDropdownComponent
                v-if="secondaryOptions.length"
                label="Opciones"
                custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                container-class="tw:w-full tw:sm:w-auto"
                :data-cy="'sesion-opciones-' + sesion.id"
                :options="secondaryOptions"
            />
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue';

    import Button from '@/modules/admin/ui/Button.vue';
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue';
    import Pill from '@/modules/admin/ui/Pill.vue';
    import { formatSesionFecha } from '@/utils/dateUtils';
    import store from '@/store';
    import { esVentaRestringida } from '@/composables/admin/events/useVentaRestringida';

    const props = defineProps({
        evento: {
            type: Object,
            required: true
        },
        sesion: {
            type: Object,
            required: true
        },
        permisos: {
            type: Object,
            default: () => ({})
        },
        isTaquilleroVisitante: {
            type: Boolean,
            default: false
        },
        /**
         * Which listado this row belongs to. Both lists share the card layout
         * and the Venta / Gestor butacas actions, but only "actuales" can
         * configure prices, edit and delete the session, while "pasados"
         * exposes its reports as buttons.
         */
        variant: {
            type: String,
            default: 'actuales',
            validator: v => ['actuales', 'pasados'].includes(v)
        }
    });

    const emit = defineEmits(['edit', 'delete', 'copy-url', 'test-venta', 'descargar-plano']);

    // Ventas restringidas puntuales: el usuario afectado no ve el botón de venta
    // de ese evento (el guard del router tampoco le deja entrar por URL).
    const puedeVenderEnEvento = computed(() => !esVentaRestringida(props.evento?.id, store.getters.getUser));

    const params = computed(() => ({
        evento_id: props.evento.id,
        sesion_id: props.sesion.id
    }));

    /**
     * Fecha de inicio de la sesión para mostrar. El v2 entrega `start_at` como
     * instante ISO 8601 (ya no preformateado), así que se formatea en la zona
     * del navegador con el helper local.
     */
    const fechaFormateada = computed(() => formatSesionFecha(props.sesion.start_at));

    /**
     * Secondary session actions collapsed into one dropdown, so the row keeps
     * a short, scannable set of primary buttons. Order and labels follow the
     * "Opciones de sesión" menu of the design reference.
     *
     * Options already promoted to a visible button for the current variant
     * are skipped, so nothing is listed twice.
     */
    const secondaryOptions = computed(() => {
        const { permisos, isTaquilleroVisitante, evento, variant } = props;
        const visitante = isTaquilleroVisitante;
        const enPasados = variant === 'pasados';
        const options = [];

        options.push({
            type: 'action',
            icon: 'copy',
            label: 'Copiar URL',
            action: () => emit('copy-url', props.sesion)
        });

        if (permisos['eventos.vender'] && !visitante) {
            options.push({
                type: 'action',
                icon: 'flask',
                label: 'Test venta',
                action: () => emit('test-venta', props.sesion)
            });
        }

        if (permisos['eventos.editar'] && !enPasados) {
            options.push({
                type: 'action',
                icon: 'file-pdf',
                label: 'Descargar plano PDF',
                action: () => emit('descargar-plano', props.sesion)
            });
        }

        if (permisos['eventos.informe'] && !visitante && !enPasados) {
            options.push({
                to: { name: 'back.events.sesion.informe.control-acceso', params: params.value },
                icon: 'chart-bar',
                label: 'Informe control de accesos'
            });
        }

        if (permisos['eventos.control_accesos'] && !visitante) {
            options.push({
                to: {
                    name: 'back.events.sesion.control-acceso',
                    params: { ...params.value, codigo_evento: evento.codigo }
                },
                icon: 'door-open',
                label: 'Control de acceso'
            });
        }

        if (permisos['eventos.editar'] && !enPasados) {
            options.push({
                to: { name: 'back.events.sesion.productos', params: params.value },
                icon: 'shopping-bag',
                label: 'Productos'
            });
            options.push({
                to: { name: 'back.events.sesion.codigos-qr', params: params.value },
                icon: 'qrcode',
                label: 'Códigos QR'
            });
        }

        if (permisos['eventos.editar'] && evento.compra_privada) {
            options.push({
                to: { name: 'back.events.sesion.compradores-permitidos', params: params.value },
                icon: 'user-check',
                label: 'Compradores permitidos'
            });
        }

        // Editar y Borrar viven en el desplegable: la fila se queda con las
        // acciones principales (Venta, Gestor butacas, Precios). Borrar va al
        // final para no quedar pegado a acciones inocuas.
        if (permisos['eventos.editar'] && !enPasados) {
            options.push({
                type: 'action',
                icon: 'edit',
                label: 'Editar',
                action: () => emit('edit', props.sesion)
            });
            options.push({
                type: 'action',
                icon: 'trash',
                label: 'Borrar',
                action: () => emit('delete', props.sesion)
            });
        }

        return options;
    });
</script>
